import { US_NAVY_ORG_PERMISSION } from 'features/navbar/orgPermissions'
import { useGeckoPermissions } from 'features/permissions'
import {
  isPermittedByGate,
  type PermissionGateSpec,
} from 'features/permissions/utils/isPermittedByGate'
import { useMemo } from 'react'
import {
  type AssetView,
  assetViewSchema,
  type ModelKind,
  PERMISSION_CANTILEVER_SYSTEM_VIEWER,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PERMISSION_MATTERPORT_WALKTHROUGH_VIEWER,
} from 'types'

export const DEFAULT_ASSET_VIEW: AssetView = 'scans'

export const ASSET_VIEW_LABEL: Record<AssetView, string> = {
  scans: 'Robot Scans',
  walkthroughs: 'Walkthroughs',
  'birds-view': 'Drone Scans',
}

/** Same gates the retired 3D Models nav item carried; `scans` is open to anyone who sees Assets. */
export const ASSET_VIEW_ACCESS: Record<AssetView, PermissionGateSpec> = {
  scans: {},
  walkthroughs: {
    requires: [
      PERMISSION_MATTERPORT_WALKTHROUGH_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  'birds-view': {
    requires: [
      PERMISSION_CANTILEVER_SYSTEM_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
}

export const MODEL_KIND_FOR_ASSET_VIEW: Partial<Record<AssetView, ModelKind>> =
  {
    walkthroughs: 'walkthrough',
    'birds-view': 'birdsView',
  }

export const assetViewForModelKind = (kind: ModelKind): AssetView =>
  kind === 'walkthrough' ? 'walkthroughs' : 'birds-view'

/**
 * The views this user may open, in picker order; `scans` is always first.
 * Undefined until the user's permissions have loaded, so callers can wait
 * rather than briefly treating everyone as unpermitted.
 */
export function usePermittedAssetViews(): AssetView[] | undefined {
  const { permissions, hasPermissions } = useGeckoPermissions()
  return useMemo(
    () =>
      permissions === undefined
        ? undefined
        : assetViewSchema.options.filter((view) =>
            isPermittedByGate(hasPermissions, ASSET_VIEW_ACCESS[view])
          ),
    [hasPermissions, permissions]
  )
}

/** The 3D model kinds this user may open, derived from the view gates. */
export function usePermittedModelKinds(): ModelKind[] | undefined {
  const views = usePermittedAssetViews()
  return useMemo(
    () =>
      views?.flatMap((view) => {
        const kind = MODEL_KIND_FOR_ASSET_VIEW[view]
        return kind ? [kind] : []
      }),
    [views]
  )
}
